import React, { useRef } from 'react'
import { isServer } from 'lib/utils'

import HorizontalScrollButtons from 'components/HorizontalScrollButtons/HorizontalScrollButtons'
import ProfileCollectionSkeleton from 'components/ProfileCollectionSkeleton/ProfileCollectionSkeleton'
import useFeaturedVideos from 'hooks/useFeaturedVideos'
import useScreenSize from 'hooks/useScreenSize'

import CardWrapper from '../CardWrapper/CardWrapper'
import Occasions from '../Occasions/Occasions'
import { EdgeProps, FeaturedVideoProps, NodeProps, VideosProps } from '../type'
import VideoCard from '../VideoCard/VideoCard'

import styles from './Videos.module.css'

const Videos: React.FC<VideosProps> = ({ profile }) => {
  const MAX_NUMBER_OF_CARDS = 8

  const screenSize = useScreenSize(isServer())
  const scrollableWrapperRef = useRef<HTMLDivElement>()

  let skeletonWidth: number | undefined
  if (screenSize.width >= 1024 && screenSize.height < 1200) {
    skeletonWidth = 265
  } else if (screenSize.width >= 1024 && screenSize.height >= 1200) {
    skeletonWidth = 330
  }

  let skeletonHeight = 262
  if (screenSize.width >= 1024 && screenSize.height < 1200) {
    skeletonHeight = 458
  } else if (screenSize.width >= 1024 && screenSize.height >= 1200) {
    skeletonHeight = 572
  }

  const { slug, profileProducts, defaultProfileProduct } = profile

  const { loading, error, featuredVideos } = useFeaturedVideos(slug)

  const profileProductNodes = loading
    ? Array(defaultProfileProduct)
    : profileProducts?.edges
        .map((edge: EdgeProps) => {
          const { node } = edge
          return node.acceptingOrders && node.showOnProfilePage && node
        })
        .filter((node: NodeProps) => node)

  if (error) return null

  if (
    featuredVideos?.length === 0 &&
    !(profile?.profileVideo?.url || profile?.profilePicture?.cardUrl)
  )
    return null

  const profileVideo = {
    id: `talent-video`,
    video: {
      url: profile?.profileVideo?.url,
      thumbnailUrl: profile?.profilePicture?.cardUrl,
    },
  }
  const videoRequests =
    featuredVideos?.map((video) => {
      const { thumbnailUrl, videoUrl, requestOccasion, review, duration } =
        video

      return {
        requestOccasion,
        review,
        video: {
          url: videoUrl,
          thumbnailUrl,
          duration,
        },
      }
    }) || []

  const productsWithExpressDelivery = profileProductNodes?.flatMap(
    (product) => {
      return product?.productChildren?.filter(
        (productChildren) =>
          productChildren.product.productType === 'EXPRESS_DELIVERY',
      )
    },
  )

  const withReviewsFirst =
    () =>
    (a: FeaturedVideoProps, b: FeaturedVideoProps): -1 | 0 | 1 => {
      if (a.review && !b.review) return -1

      if (!a.review && b.review) return 1

      return 0
    }

  return (
    <section className={styles.wrapper}>
      <div className={styles.scrollWrapper} ref={scrollableWrapperRef}>
        <CardWrapper>
          <VideoCard
            request={profileVideo}
            typicallyRespondsInDays={profile.typicallyRespondsInDays ?? 3}
            productsWithExpressDelivery={productsWithExpressDelivery}
            hideButton
          />
        </CardWrapper>

        {loading ? (
          <CardWrapper>
            <div className={styles.loading}>
              <ProfileCollectionSkeleton
                amount={featuredVideos?.length || 0}
                width={skeletonWidth}
                height={skeletonHeight}
                marginRight={16}
              />
            </div>
          </CardWrapper>
        ) : (
          <>
            {[...videoRequests].sort(withReviewsFirst()).map((request) => (
              <CardWrapper key={`videoRequest-${request.video.url}`}>
                <VideoCard
                  request={request}
                  typicallyRespondsInDays={profile.typicallyRespondsInDays}
                  profileSlug={slug}
                />
              </CardWrapper>
            ))}
            {featuredVideos && (
              <Occasions
                videos={featuredVideos}
                profileName={profile.name}
                profileSlug={profile.slug}
                occasionsToShow={MAX_NUMBER_OF_CARDS - featuredVideos.length}
              />
            )}
          </>
        )}
      </div>

      <div className={styles.scrollButtonsWrapper}>
        <HorizontalScrollButtons
          scrollableWrapperRef={scrollableWrapperRef}
          scrollStep={520}
        />
      </div>
    </section>
  )
}

export default Videos
